import { Injectable } from '@angular/core';
import $ from 'jquery';
import compare from 'just-compare';
import moment from 'moment';
import { Subject } from 'rxjs';
import { ResponseStatus } from './shared';
import { LoggingService } from './shared/services/logging/logging.service';

const log = new LoggingService();
let ts: TabulatorService;

@Injectable({
    providedIn: 'root'
})
/// Contains any logic needed for display or conversion for tabulator
export class TabulatorService {
    // TODO: strongly type this to a RowComponent if possible
    private rowChangedSource = new Subject<any>();
    public rowChanged$ = this.rowChangedSource.asObservable();
    private currentRowChanged = false;
    private _tabulator: any;
    public set tabulator(value: any) {
        this._tabulator = value;
        log.debug({
            type: 'TabulatorService:tabulator:set',
            tabulator: this._tabulator
        });
        // Force border styles after tabulator is set
        this.ensureBorderStyles();
    }
    private currentRow: any;
    constructor() {
        ts = this;
        $(document).on('click', function (evt: any) {
            log.debug({
                type: 'TabulatorService:document:click',
                evt,
                tabulator: ts._tabulator
            });
            if (ts._tabulator && ts.currentRow) {
                if (
                    !ts._tabulator.is(evt.target) && // if the target of the click isn't the container...
                    ts._tabulator.has(evt.target).length === 0 && // ... nor a descendant of the container
                    !evt.target.className.includes('tabulator')
                ) {
                    log.debug({
                        type: 'TabulatorService:document:click:outside',
                        target: evt.target,
                        tabulator: ts._tabulator
                    });
                    ts.checkForDifferentRow(undefined, undefined);
                }
            }
        });
    }

    // INFO: be careful with the below, they should be wired up to tabulator events/editors,
    // and that makes "this" point to a tabulator instance, not a TabulatorService instance
    // cell - the cell component for the editable cell
    // onRendered - function to call when the editor has been rendered
    // success - function to call to pass the successfuly updated value to Tabulator
    // cancel - function to call to abort the edit and return to a normal cell
    // editorParams - params object passed into the editorParams column definition property
    public cellEdited(cell) {
        log.debug({
            type: 'TabulatorService:cellEdited',
            cell,
            tabulator: ts._tabulator
        });
        const row = cell.getRow();
        const data = cell.getData();
        ts.checkForDifferentRow(row, cell, true);
        ts.currentRow = row;

        if (
            data.Status &&
            !(
                data.Status.StatusCode === ResponseStatus.Success ||
                data.Status.StatusCode === ResponseStatus.Loading
            )
        ) {
            row.update({
                Status: { StatusCode: ResponseStatus.None, Message: '' }
            });
        }
    }
    public cellEditing(cell) {
        log.debug({
            type: 'TabulatorService:cellEditing',
            cell,
            tabulator: ts._tabulator
        });
        ts.checkForDifferentRow(cell.getRow(), cell);
    }
    public cellEditCancelled(cell) {
        log.debug({
            type: 'TabulatorService:cellEditCancelled',
            cell,
            tabulator: ts._tabulator
        });
        const row = cell.getRow();
        const data = cell.getData();
        if (
            data.Status &&
            !(
                data.Status.StatusCode === ResponseStatus.Success ||
                data.Status.StatusCode === ResponseStatus.Loading
            )
        ) {
            row.update({
                Status: { StatusCode: ResponseStatus.None, Message: '' }
            });
        }
    }
    public checkForDifferentRow(row, cell, updated = false) {
        log.verbose({
            type: 'TabulatorService:checkForDifferentRow',
            updated,
            value: cell && cell.getValue(),
            row,
            cell
        });
        ts.currentRowChanged = ts.currentRowChanged || updated;
        if (ts.currentRow) {
            const currentData = ts.currentRow.getData();
            const rowData = (row && row.getData()) || undefined;
            // TODO: would be nice if we could just check an ID here.
            if (compare(currentData, rowData)) {
                log.verbose({
                    type: 'TabulatorService:checkForDifferentRow',
                    currentData,
                    rowData
                });
                if (cell) {
                    log.verbose({
                        type: 'TabulatorService:checkForDifferentRow-cancelEdit',
                        cell
                    });
                    cell.cancelEdit();
                }
                if (ts.currentRowChanged) {
                    ts.rowChangedSource.next(ts.currentRow);
                    ts.currentRowChanged = false;
                }
                ts.currentRow = undefined;
            }
        }
    }
    public cellMouseHandler(e, cell) {
        ts.checkForDifferentRow(cell.getRow(), cell);
    }
    public rowMouseHandler(e, row) {
        ts.checkForDifferentRow(row, null);
    }
    // custom editors
    public dateEditor(cell, onRendered, success, cancel, editorParams) {
        const editor = document.createElement('input');

        editor.setAttribute('type', 'date');

        // style input
        editor.style.padding = '3px';
        editor.style.width = '100%';
        editor.style.boxSizing = 'border-box';

        // Set value of editor to the current value of the cell
        editor.value = moment(cell.getValue()).format('YYYY-MM-DD');
        // editor.value = cell.getValue();

        // set focus on the select box when the editor is selected (timeout allows for editor to be added to DOM)
        onRendered(function () {
            editor.focus();
            // editor.style.css = '100%';
        });

        // when the value has been set, trigger the cell to update
        function successFunc() {
            const newDate = moment(editor.value);
            const oldDate = moment(cell.getValue());
            const oldDateDayBefore = moment(cell.getValue());
            oldDateDayBefore.subtract(1, 'days');
            log.debug({
                type: 'TabulatorService:dateEditor:successFunc',
                oldDate,
                newDate,
                oldDateDayBefore
            });
            if (
                newDate.isSame(oldDate) ||
                (newDate.isAfter(oldDateDayBefore) && newDate.isBefore(oldDate))
            ) {
                log.debug({
                    type: 'TabulatorService:dateEditor:successFunc:cancel()'
                });
                cancel();
            } else {
                log.debug({
                    type: 'TabulatorService:dateEditor:successFunc:success()'
                });
                try {
                    success(moment(editor.value).toDate());
                } catch (e) {
                    cancel();
                }
            }
        }

        editor.addEventListener('blur', successFunc);

        // return the editor element
        return editor;
    }
    public numberOnly(cell, onRendered, success, cancel, editorParams) {
        const editor = document.createElement('input');
        const precision = editorParams.precision || 2;
        editor.setAttribute('type', 'text');
        // style input
        editor.style.padding = '3px';
        editor.style.width = '100%';
        editor.style.boxSizing = 'border-box';
        editor.value = cell.getValue();

        onRendered(function () {
            editor.focus();
            // editor.style.css = '100%';
        });
        function successFunc() {
            success(editor.value);
        }
        function validateNumber(e) {
            const reg = new RegExp(`^\\d+(\\.\\d{0,${precision}})?$`);
            if (e.keyCode === 8) {
                return;
            }
            if (!e.key.match(/^[\d.]$/)) {
                e.preventDefault();
                return;
            }
            if (!(editor.value + e.key).match(reg)) {
                e.preventDefault();
            }
        }
        editor.addEventListener('blur', successFunc);
        editor.addEventListener('keypress', validateNumber);
        editor.addEventListener('keydown', validateNumber);

        return editor;
    }
    public password(cell, onRendered, success, cancel, editorParams) {
        const editor = document.createElement('input');

        editor.setAttribute('type', 'password');
        editor.style.width = '100%';
        editor.style.height = '100%';
        editor.style.boxSizing = 'border-box';
        editor.value = cell.getValue();

        // set focus on the select box when the editor is selected (timeout allows for editor to be added to DOM)
        onRendered(function () {
            editor.focus();
            // editor.style.css = '100%';
        });

        // when the value has been set, trigger the cell to update
        function successFunc() {
            try {
                success(editor.value);
            } catch (e) {
                cancel();
            }
        }

        editor.addEventListener('blur', successFunc);

        // return the editor element
        return editor;
    }
    public validationFailed(cell, value, validators) {
        const row = cell.getRow();
        row.update({
            Status: {
                StatusCode: ResponseStatus.Failed,
                Message: `${value} is invalid.`
            }
        });
    }

    /**
     * Public method to force border styles on all tabulator instances
     * Call this after theme changes or when borders disappear
     */
    public forceBorderStyles() {
        this.ensureBorderStyles();
    }

    /**
     * Ensures border-right styles are applied to Tabulator elements
     * This method helps prevent border styles from disappearing after page refresh
     */
    private ensureBorderStyles() {
        // Use setTimeout to ensure the DOM is ready
        setTimeout(() => {
            try {
                // Get the border color from CSS variables with fallback
                const computedStyle = getComputedStyle(document.documentElement);
                const borderColor = computedStyle.getPropertyValue('--p-content-border-color').trim() || '#d1d5db';

                // Apply border styles to all tabulator cells and headers
                const tabulatorElements = document.querySelectorAll('.tabulator');
                tabulatorElements.forEach(tabElement => {
                    // Header columns
                    const headerCols = tabElement.querySelectorAll('.tabulator-header .tabulator-col');
                    headerCols.forEach(col => {
                        (col as HTMLElement).style.borderRight = `1px solid ${borderColor}`;
                    });

                    // Data cells
                    const cells = tabElement.querySelectorAll('.tabulator-table .tabulator-row .tabulator-cell');
                    cells.forEach(cell => {
                        (cell as HTMLElement).style.borderRight = `1px solid ${borderColor}`;
                    });
                });

                log.debug({
                    type: 'TabulatorService:ensureBorderStyles',
                    borderColor,
                    elementsFound: tabulatorElements.length
                });
            } catch (error) {
                log.error({
                    type: 'TabulatorService:ensureBorderStyles:error',
                    error
                });
            }
        }, 100);
    }
}
